import { InputNumberLocalized } from '@components'

import {
  Button,
  ButtonsGroup,
  ComboBox,
  Flex,
  FormItem as FormItemDSComponent,
  FormItemsGroup,
  Icon,
  Modal,
  ModalProps,
  Space,
  Spacing,
  Text,
  Tooltip,
  withModalConditionalRender,
} from '@billy/design-system'
import isMatch from 'lodash/isMatch'
import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { DEFAULT_INVOICE_PREFIX } from '@features/invoices/constants/defaultInvoicePrefix'
import { MINIMAL_INVOICE_NUMBER } from '@features/invoices/constants/minimalInvoiceNumber'

import { useDebouncedCallback } from '../../../../../../../../../../../../../../hooks/useDebouncedCallback'
import { useInvoiceNumberingSequences } from '../../../../../../../../../../../../hooks/useInvoiceNumberingSequences'
import { useInvoiceEditorForm } from '../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useInvoicePrefixAndNumber } from '../../../../contexts/invoicePrefixAndNumberContext'
import { InvoicePrefixDescription } from './elements/InvoicePrefixDescription'

// make sure we operate on FE values (no backend nulls)
const prefixValue = (value: string | null | undefined) => value ?? DEFAULT_INVOICE_PREFIX

// BE null and FE '' represent the same empty prefix
const doesSequenceMatchPrefix = (sequence: string | null, prefix: string | undefined) =>
  sequence === prefix || (sequence === null && prefix === '')

const formatComboBoxValue = (value: string) => value.toUpperCase().trim()

const InvoicePrefixAndNumberModalComponent = (modalProps: ModalProps): ReactElement | null => {
  const { t } = useTranslation()
  const { invoiceNumberingSequences } = useInvoiceNumberingSequences()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { closeInvoicePrefixAndNumberModal, getDocumentDisplayedTitle, saveInvoicePrefixAndNumber } =
    useInvoicePrefixAndNumber()
  const { FormItem, getValues, setValue, trigger, watch } = invoiceEditorForm
  const [prefixPreview, setPrefixPreview] = useState(getValues('invoiceNumberPrefix'))

  const { invoiceNumberPrefix, invoiceNumber } = watch()

  const invoiceTitlePreview = getDocumentDisplayedTitle(prefixPreview)

  const invoicePrefixItems = useMemo(
    () =>
      invoiceNumberingSequences.map((invoice, id) => ({
        id: String(id),
        children: prefixValue(invoice.prefix),
        value: prefixValue(invoice.prefix),
      })),
    [invoiceNumberingSequences],
  )

  const hasSelectedExistingSequence = useMemo(() => {
    return invoiceNumberingSequences.some((sequence) => doesSequenceMatchPrefix(sequence.prefix, invoiceNumberPrefix))
  }, [invoiceNumberPrefix, invoiceNumberingSequences])

  // Default sequence (base=1, next=1, prefix=null) can have its base overridden before any invoice is created
  const isDefaultOverridableSequence = useMemo(() => {
    const selected = invoiceNumberingSequences.find((sequence) =>
      doesSequenceMatchPrefix(sequence.prefix, invoiceNumberPrefix),
    )
    return !!selected && isMatch(selected, { base: 1, next: 1, prefix: null })
  }, [invoiceNumberPrefix, invoiceNumberingSequences])

  const handleDebouncedInvoicePrefixAndNumberInputChange = useDebouncedCallback(() =>
    trackInvoiceEditor('Form - Invoice number modal - Prefix typed'),
  )

  // Ensure stable function: InputNumberLocalized triggers onChange multiple times
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableTrackInvoiceEditor = useCallback(trackInvoiceEditor, [])

  const handleDebounceNumberLocalizedInputChange = useDebouncedCallback(() =>
    stableTrackInvoiceEditor('Form - Invoice number modal - Invoice number typed'),
  )

  const handleInvoiceNumberPrefixChange = useCallback(
    (typedPrefix: string) => {
      const normalizedPrefix = typedPrefix.trim()
      const selectedInvoiceNumberPrefixSequence = invoiceNumberingSequences.find((sequence) =>
        doesSequenceMatchPrefix(sequence.prefix, normalizedPrefix),
      )

      if (normalizedPrefix === '') {
        setValue('invoiceNumberPrefix', normalizedPrefix)
      }

      if (selectedInvoiceNumberPrefixSequence) {
        setValue('invoiceNumber', selectedInvoiceNumberPrefixSequence.next)
      } else {
        setValue('invoiceNumber', MINIMAL_INVOICE_NUMBER)
      }
    },
    [invoiceNumberingSequences, setValue],
  )

  const handleCancelButtonClick = useCallback(() => {
    closeInvoicePrefixAndNumberModal()
  }, [closeInvoicePrefixAndNumberModal])

  const handleSaveButtonClick = useCallback(() => {
    saveInvoicePrefixAndNumber()
  }, [saveInvoicePrefixAndNumber])

  const handleInvoicePrefixUpdate = useCallback(
    (onChange: (value: string) => void) => (value: string) => {
      setPrefixPreview(value)
      const normalizedValue = formatComboBoxValue(value)
      handleInvoiceNumberPrefixChange(normalizedValue)
      onChange(normalizedValue)
      trigger('invoiceNumberPrefix')
    },
    [handleInvoiceNumberPrefixChange, trigger],
  )

  const handleInputChange = useCallback(
    (onChange: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
      handleDebouncedInvoicePrefixAndNumberInputChange()
      const eventValue = event.target.value
      handleInvoicePrefixUpdate(onChange)(eventValue)
    },
    [handleDebouncedInvoicePrefixAndNumberInputChange, handleInvoicePrefixUpdate],
  )

  return (
    <Modal size="s" {...modalProps}>
      <Modal.Header title={t('external_invoices.editor.invoice_prefix_and_number_modal.title')} />
      <Modal.Body>
        <FormItemsGroup itemsInRow={21}>
          <FormItem
            name="invoiceNumberPrefix"
            itemColumnStart={1}
            itemColumnEnd={11}
            label={
              <Flex flexDirection="row" gap={Spacing.S4}>
                <Text colorVariant="secondary">
                  {t('external_invoices.editor.invoice_prefix_and_number_modal.prefix.label')}
                </Text>
                <Tooltip
                  label={t('external_invoices.editor.invoice_prefix_and_number_modal.prefix.tooltip')}
                  placement="top"
                >
                  <Icon icon="circleQuestionMark" size="xs" />
                </Tooltip>
              </Flex>
            }
            render={({ field: { value, onChange, ...props } }) => {
              return (
                <ComboBox
                  {...props}
                  formatDisplayedValue={formatComboBoxValue}
                  initialInputValue={prefixValue(value)}
                  isMultiselect={false}
                  items={invoicePrefixItems}
                  onChange={handleInvoicePrefixUpdate(onChange)}
                  onInputChange={handleInputChange(onChange)}
                  shouldDisplayItemValue={false}
                  size="sm"
                />
              )
            }}
          />
          <FormItemDSComponent itemColumnStart={11} itemColumnEnd={12} label="&nbsp;">
            <Space size="s16" />
            <Flex justifyContent="center" alignContent="center">
              <Text>&mdash;</Text>
            </Flex>
          </FormItemDSComponent>
          <FormItem
            name="invoiceNumber"
            itemColumnStart={12}
            itemColumnEnd={22}
            label={t('external_invoices.editor.invoice_prefix_and_number_modal.number.label')}
            render={({ field: { value, onChange, ...props } }) => {
              return (
                <InputNumberLocalized
                  {...props}
                  alignment="left"
                  disabled={hasSelectedExistingSequence && !isDefaultOverridableSequence}
                  onChange={(event) => {
                    onChange(event)
                    trigger('invoiceNumber')
                    handleDebounceNumberLocalizedInputChange()
                  }}
                  size="sm"
                  step={1}
                  type="number"
                  value={invoiceNumber}
                  withFormatting={false}
                />
              )
            }}
          />
        </FormItemsGroup>
        <Trans
          i18nKey="external_invoices.editor.invoice_prefix_and_number_modal.numbering_preview"
          values={{ invoiceTitle: invoiceTitlePreview }}
        >
          <Text colorVariant="discreet">
            Numbering is automatic. Your next invoice will be:
            <Text as="span" colorVariant="primary" nowrap uppercase>
              {invoiceTitlePreview}
            </Text>
          </Text>
        </Trans>
        <Space size="s16" />
        <InvoicePrefixDescription />
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button variant="text" onClick={handleCancelButtonClick}>
            {t('cancel')}
          </Button>
          <Button onClick={handleSaveButtonClick}>{t('save')}</Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}

export const InvoicePrefixAndNumberModal = withModalConditionalRender(InvoicePrefixAndNumberModalComponent)
